import React, { useEffect, useRef, useState } from "react";
import { Box, Card, CardContent, CircularProgress, Typography } from "@mui/material";
import { useParams } from "react-router-dom";
import PndRestApi from "./utils/pnd/PndRestApi";
import { OPERATION_TYPE_FOR_DROP_OFF_ORDER_CREATION, OPERATION_TYPE_FOR_GOODS_RECEIVED, OPERATION_TYPE_FOR_PGI, PND_LIVE_LOCATION_REFRESH_INTERVAL, SCS_ORDER_TYPE_STANDARAD } from "./utils/pnd/PndConstants";

export default function MapTracker() {
  const mapRef = useRef(null);
  const googleMap = useRef(null);
  const driverMarker = useRef(null);
  const deliveryMarker = useRef(null);
  const { orderNo } = useParams();
  const isInitialMap = useRef(true);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState("");

  const lastPosition = useRef(null);
  const lastAngle = useRef(null);
  const hasArrivedRef = useRef(false);
  const [trackingInfo, setTrackingInfo] = useState(null);

  const directionsService = useRef(null);
  const directionsRenderer = useRef(null);
  const polyline = useRef(null);

  /** Calculate distance in meters using Haversine formula */
  const calculateDistance = (lat1, lng1, lat2, lng2) => {
    const R = 6371e3; // Earth radius in meters
    const phi1 = (lat1 * Math.PI) / 180;
    const phi2 = (lat2 * Math.PI) / 180;
    const deltaPhi = ((lat2 - lat1) * Math.PI) / 180;
    const deltaLambda = ((lng2 - lng1) * Math.PI) / 180;

    const a =
      Math.sin(deltaPhi / 2) * Math.sin(deltaPhi / 2) +
      Math.cos(phi1) * Math.cos(phi2) *
      Math.sin(deltaLambda / 2) * Math.sin(deltaLambda / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    return R * c; // in meters
  };

  /** Calculate bearing between two points */
  const calculateBearing = (startLat, startLng, endLat, endLng) => {
    const startLatRad = (startLat * Math.PI) / 180;
    const startLngRad = (startLng * Math.PI) / 180;
    const endLatRad = (endLat * Math.PI) / 180;
    const endLngRad = (endLng * Math.PI) / 180;

    const y = Math.sin(endLngRad - startLngRad) * Math.cos(endLatRad);
    const x =
      Math.cos(startLatRad) * Math.sin(endLatRad) -
      Math.sin(startLatRad) * Math.cos(endLatRad) * Math.cos(endLngRad - startLngRad);

    let brng = (Math.atan2(y, x) * 180) / Math.PI;
    return (brng + 360) % 360;
  };

  const generateRotatedIcon = (imageUrl, angle) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = 100;
        canvas.height = 100;
        const ctx = canvas.getContext("2d");

        ctx.translate(50, 50);
        ctx.rotate((angle * Math.PI) / 180);
        ctx.drawImage(img, -35, -35, 70, 70);

        const dataUrl = canvas.toDataURL();
        resolve(dataUrl);
      };
      img.onerror = () => {
        resolve(imageUrl);
      };
      img.src = imageUrl;
    });
  };

  /** Load Google Maps script */
  const loadGoogleMaps = () => {
    return new Promise((resolve) => {
      if (window.google) return resolve();
      const script = document.createElement("script");
      script.src =
        "https://maps.googleapis.com/maps/api/js?key=AIzaSyBHsahju6fIxbLa4AtLLPtw6et6k4mb0mk";
      script.async = true;
      script.defer = true;
      script.onload = resolve;
      document.body.appendChild(script);
    });
  };

  const fetchOrderDetails = async () => {
    try {
      const response = await PndRestApi.GetRiderLiveTracking(orderNo);
      if (response.status === 200 && response.data.status === 200)
        return response.data.data;
    } catch (err) {
      console.error(err);
      return null;
    }
  };

  /** Initialize or update map */
  const updateMap = async () => {
    const data = await fetchOrderDetails();
    if (loading) setLoading(false);

    if (!data?.responseStatus) {
      setErrorMsg(data?.message ? data?.message : "Unable to load order details");
      return;
    }

    const driverLatLng = {
      lat: parseFloat(data.riderLat),
      lng: parseFloat(data.riderLon),
    };

    const deliveryLatLng = {
      lat: parseFloat(data.custLat),
      lng: parseFloat(data.custLon),
    };

    // Calculate distance between vehicle and delivery location
    const dist = calculateDistance(
      driverLatLng.lat,
      driverLatLng.lng,
      deliveryLatLng.lat,
      deliveryLatLng.lng
    );

    setTrackingInfo({
      riderName: data.riderName,
      orderNumber: data.orderNumber,
      invoiceNo: data.invoiceNo,
      orderStatus: data.orderStatus,
      distance: dist,
    });

    /** Create map only once */
    if (isInitialMap.current) {
      googleMap.current = new window.google.maps.Map(mapRef.current, {
        mapTypeId: window.google.maps.MapTypeId.ROADMAP,
        disableDefaultUI: true,
        zoomControl: true,
      });
    }

    // Determine the bearing (heading) of the vehicle
    let currentAngle = lastAngle.current;

    if (lastPosition.current) {
      const distanceMoved = calculateDistance(
        lastPosition.current.lat,
        lastPosition.current.lng,
        driverLatLng.lat,
        driverLatLng.lng
      );

      // If the driver has moved significantly (more than 5 meters), update bearing based on motion
      if (distanceMoved > 5) {
        currentAngle = calculateBearing(
          lastPosition.current.lat,
          lastPosition.current.lng,
          driverLatLng.lat,
          driverLatLng.lng
        );
      }
    }

    // Fallback if no calculated angle yet (initial load or stationary)
    if (currentAngle === null || currentAngle === undefined) {
      const apiAngle = parseFloat(data.angle);
      if (!isNaN(apiAngle) && apiAngle !== 0) {
        currentAngle = apiAngle;
      } else {
        // Point towards destination as final fallback
        currentAngle = calculateBearing(
          driverLatLng.lat,
          driverLatLng.lng,
          deliveryLatLng.lat,
          deliveryLatLng.lng
        );
      }
    }

    // Save for next update
    lastPosition.current = driverLatLng;
    lastAngle.current = currentAngle;

    // Offset by +90 because the van/bike icons face West in the PNG
    const iconRotationAngle = (currentAngle + 90) % 360;

    const isBeforeDelivery =
      data.orderStatusId === OPERATION_TYPE_FOR_DROP_OFF_ORDER_CREATION ||
      data.orderStatusId === OPERATION_TYPE_FOR_PGI ||
      data.orderStatusId === OPERATION_TYPE_FOR_GOODS_RECEIVED;

    const baseIcon = data.orderType === SCS_ORDER_TYPE_STANDARAD
      ? "/img/pnd/Bike_Icon_Delivery_Van.png"
      : "/img/pnd/Bike_Icon_Delivery _Bike.png";
    const storeIcon = "/img/pnd/hub.png";
    const currentRiderIcon = await generateRotatedIcon(baseIcon, iconRotationAngle);

    /** Driver / Store marker icon configuration
     * - Hub pin natural aspect ratio is 579:944 (~0.6133).
     *   Scaled to 43x70 px to match the destination pin height.
     *   Bottom needle tip anchor is at Point(21.5, 70) for exact geographic precision.
     * - Rider vehicle is a rotating square sprite with center anchor at Point(40, 40).
     */
    const driverMarkerIcon = isBeforeDelivery
      ? {
          url: storeIcon,
          scaledSize: new window.google.maps.Size(43, 70),
          anchor: new window.google.maps.Point(21.5, 70),
        }
      : {
          url: currentRiderIcon,
          scaledSize: new window.google.maps.Size(80, 80),
          anchor: new window.google.maps.Point(40, 40),
        };

    /** Driver marker */
    if (driverMarker.current) {
      driverMarker.current.setPosition(driverLatLng);
      driverMarker.current.setIcon(driverMarkerIcon);
    } else {
      driverMarker.current = new window.google.maps.Marker({
        position: driverLatLng,
        map: googleMap.current,
        icon: driverMarkerIcon,
      });
    }

    /** Delivery marker (New needle pin: 650x1019 aspect ratio, scaled to 45x70 px, needle tip anchor at Point(22.8, 67.4)) */
    const deliveryMarkerIcon = {
      url: "/img/pnd/3dicons-map-pin-front-color.svg",
      scaledSize: new window.google.maps.Size(45, 70),
      anchor: new window.google.maps.Point(22.8, 67.4),
    };

    if (deliveryMarker.current) {
      deliveryMarker.current.setPosition(deliveryLatLng);
      deliveryMarker.current.setIcon(deliveryMarkerIcon);
    } else {
      deliveryMarker.current = new window.google.maps.Marker({
        position: deliveryLatLng,
        map: googleMap.current,
        icon: deliveryMarkerIcon,
      });
    }

    /** Direction Line handling */
    if (isBeforeDelivery) {
      // Before delivery starts: Hide/remove direction line
      if (directionsRenderer.current) {
        directionsRenderer.current.setMap(null);
      }
      if (polyline.current) {
        polyline.current.setMap(null);
      }
    } else {
      // After rider starts delivery: Display direction line connecting rider location and delivery location
      if (!directionsService.current) {
        directionsService.current = new window.google.maps.DirectionsService();
      }
      if (!directionsRenderer.current) {
        directionsRenderer.current = new window.google.maps.DirectionsRenderer({
          map: googleMap.current,
          suppressMarkers: true,
          polylineOptions: {
            strokeColor: "#1976d2",
            strokeWeight: 5,
            strokeOpacity: 0.8,
          },
        });
      } else {
        directionsRenderer.current.setMap(googleMap.current);
      }

      directionsService.current.route(
        {
          origin: driverLatLng,
          destination: deliveryLatLng,
          travelMode: window.google.maps.TravelMode.DRIVING,
        },
        (result, status) => {
          if (status === window.google.maps.DirectionsStatus.OK) {
            if (polyline.current) {
              polyline.current.setMap(null);
            }
            directionsRenderer.current.setMap(googleMap.current);
            directionsRenderer.current.setDirections(result);
          } else {
            // Fallback to straight polyline if Directions API call fails or is un-routable
            if (directionsRenderer.current) {
              directionsRenderer.current.setMap(null);
            }
            if (!polyline.current) {
              polyline.current = new window.google.maps.Polyline({
                path: [driverLatLng, deliveryLatLng],
                geodesic: true,
                strokeColor: "#1976d2",
                strokeOpacity: 0.8,
                strokeWeight: 5,
                map: googleMap.current,
              });
            } else {
              polyline.current.setPath([driverLatLng, deliveryLatLng]);
              polyline.current.setMap(googleMap.current);
            }
          }
        }
      );
    }

    /** Adjust map center and zoom based on distance */
    if (dist > 50) {
      // Vehicle is still on the way: fit bounds to show both markers
      const bounds = new window.google.maps.LatLngBounds();
      bounds.extend(driverLatLng);
      bounds.extend(deliveryLatLng);

      googleMap.current.fitBounds(bounds);

      // Limit maximum zoom level during auto-bounds so it doesn't over-zoom when far-to-medium distance
      const zoomListener = window.google.maps.event.addListener(googleMap.current, "zoom_changed", () => {
        if (googleMap.current.getZoom() > 16) {
          googleMap.current.setZoom(16);
        }
        window.google.maps.event.removeListener(zoomListener);
      });

      hasArrivedRef.current = false;
    } else {
      // Vehicle has arrived (within 50 meters of the delivery location)
      if (!hasArrivedRef.current) {
        // First arrival event: center on delivery location and zoom in to street level (level 17)
        googleMap.current.setCenter(deliveryLatLng);
        googleMap.current.setZoom(17);
        hasArrivedRef.current = true;
      }
      // On subsequent updates while within 50m, we do NOT change zoom or center, 
      // allowing the user full control to zoom in/out/pan freely.
    }

    isInitialMap.current = false;
  };

  /** Hide Google reCAPTCHA badge on MapTracker page */
  useEffect(() => {
    const style = document.createElement("style");
    style.id = "hide-grecaptcha-style";
    style.innerHTML = ".grecaptcha-badge { visibility: hidden !important; opacity: 0 !important; pointer-events: none !important; }";
    document.head.appendChild(style);

    return () => {
      const existingStyle = document.getElementById("hide-grecaptcha-style");
      if (existingStyle) {
        existingStyle.remove();
      }
    };
  }, []);

  /** Component Mount */
  useEffect(() => {
    loadGoogleMaps().then(() => {
      updateMap();
      const interval = setInterval(updateMap, PND_LIVE_LOCATION_REFRESH_INTERVAL);
      return () => clearInterval(interval);
    });
  }, []);

  return (
    <Card
      elevation={4}
      sx={{
        height: "100vh",
        width: "100%",
        position: "relative",
      }}
    >
      {/* Floating Info Overlay */}
      {trackingInfo && (
        <Box
          sx={{
            position: "absolute",
            top: 16,
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 1000,
          }}
        >
          <Card
            sx={{
              background: "rgba(255, 255, 255, 0.9)",
              backdropFilter: "blur(8px)",
              borderRadius: "24px",
              boxShadow: "0 10px 15px -3px rgba(0,0,0,0.1), 0 4px 6px -2px rgba(0,0,0,0.05)",
              border: "1px solid rgba(226, 232, 240, 0.8)",
              px: 3,
              py: 1,
            }}
          >
            <Typography variant="body1" sx={{ fontWeight: 800, color: "#0f172a", whiteSpace: "nowrap" }}>
              Distance to destination:{" "}
              <span style={{ color: "#0ea5e9" }}>
                {trackingInfo.distance > 1000
                  ? `${(trackingInfo.distance / 1000).toFixed(2)} km`
                  : `${Math.round(trackingInfo.distance)} meters`}
              </span>
            </Typography>
          </Card>
        </Box>
      )}

      <CardContent sx={{ p: 0, height: "100%" }}>
        {loading && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "rgba(255,255,255,0.7)",
              zIndex: 10,
            }}
          >
            <CircularProgress size={60} />
          </Box>
        )}

        {errorMsg && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 20,
              backgroundColor: "white",
            }}
          >
            <Typography variant="h6" color="error" fontWeight="bold">
              {errorMsg}
            </Typography>
          </Box>
        )}

        {/* MAP CONTAINER */}
        <Box
          id="map"
          ref={mapRef}
          sx={{
            height: "100%",
            width: "100%",
          }}
        />
      </CardContent>
    </Card>
  );
}
